import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Clock, Coffee, Target, GitMerge, Receipt, Tag, Share2, FileText, Database, Package, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import { useLang } from '../lib/i18n';
import logo from '../assets/calum_logo_white.png';

const NAV = [
  {
    label: 'Prestaties',
    items: [
      { to: '/overview', label: 'Overzicht', icon: LayoutDashboard },
      { to: '/traffic', label: 'Drukte & Tijden', icon: Clock },
      { to: '/receipts', label: 'Kassabonnen', icon: Receipt },
    ],
  },
  {
    label: 'Producten',
    items: [
      { to: '/products', label: 'Productgroepen', icon: Coffee },
      { to: '/menu', label: 'Menu-analyse', icon: Target },
      { to: '/composition', label: 'Prijzen & korting', icon: Tag },
      { to: '/relations', label: 'Productrelaties', icon: Share2 },
    ],
  },
  {
    label: 'Beheer',
    items: [
      { to: '/packaging', label: 'Voorraadbeheer', icon: Package },
      { to: '/mapping', label: 'Productkoppeling', icon: GitMerge },
      { to: '/reports', label: 'Rapporten', icon: FileText },
      { to: '/data', label: 'Data', icon: Database },
    ],
  },
];

export default function Sidebar({ children }) {
  const [open, setOpen] = useState(false);
  const { logout, user } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();

  const Item = ({ to, label, icon: Icon }) => (
    <NavLink
      to={to}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          isActive
            ? 'bg-white/10 text-[#cf9a40] font-semibold'
            : 'text-emerald-50/80 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>{t(label)}</span>
    </NavLink>
  );

  const Panel = (
    <div className="flex h-full w-64 flex-col bg-[#114817] text-white">
      <button onClick={() => navigate('/overview')} className="flex items-center gap-2 px-5 py-5 text-left">
        <img src={logo} alt="Calum" className="h-9 w-auto" />
        <div className="leading-tight">
          <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>Perfy · Calum</div>
          <div className="text-[10px] text-emerald-100/60">café analytics</div>
        </div>
      </button>
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {NAV.map((group, i) => (
          <div key={group.label} className={i > 0 ? 'mt-4' : ''}>
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-100/40">
              {t(group.label)}
            </div>
            <div className="space-y-1">
              {group.items.map((n) => <Item key={n.to} {...n} />)}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 px-3 py-3">
        <div className="flex gap-1 px-1 pb-3">
          {['nl', 'fr'].map((l) => (
            <button key={l} onClick={() => setLang(l)}
              className={`flex-1 rounded-md py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                lang === l ? 'bg-[#cf9a40] text-white' : 'bg-white/5 text-emerald-50/70 hover:bg-white/10'}`}>
              {l === 'nl' ? 'NL' : 'FR'}
            </button>
          ))}
        </div>
        <div className="px-2 pb-2 text-[10px] text-emerald-100/50 truncate">{user?.email || user?.username || ''}</div>
        <button onClick={() => logout()} className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm text-emerald-50/80 hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> {t('Afmelden')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden md:block print:hidden fixed inset-y-0 left-0 z-30">{Panel}</div>
      {/* Mobile toggle */}
      <button onClick={() => setOpen(true)} aria-label="Menu"
        className="md:hidden print:hidden fixed top-2.5 left-3 z-40 rounded-lg bg-[#114817] p-2.5 text-white shadow-lg">
        <Menu className="h-5 w-5" />
      </button>
      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 max-w-[85vw]">
            {Panel}
            <button onClick={() => setOpen(false)} aria-label="Sluiten"
              className="absolute top-3 right-3 rounded-lg bg-white/10 p-2.5 text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      <div className="flex-1 md:ml-64 print:ml-0 min-w-0">{children}</div>
    </div>
  );
}
